import React, { useState, useRef } from 'react';
import Icon from '../../../components/AppIcon';

const EmailIdentifier = ({ email, setEmail, onLoad, loading, error, loaded }) => {
  const [emailError, setEmailError] = useState('');
  const [emailTouched, setEmailTouched] = useState(false);
  const [emailValid, setEmailValid] = useState(false);
  const [shake, setShake] = useState(false);
  const inputRef = useRef(null);

  const validateEmail = (val) => {
    if (!val?.trim()) return 'E-postadresse er påkrevd.';
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex?.test(val?.trim())) return 'Ugyldig e-postformat. Eksempel: navn@domene.no';
    return '';
  };

  const handleBlur = () => {
    setEmailTouched(true);
    const err = validateEmail(email);
    setEmailError(err);
    setEmailValid(!err);
  };

  const handleChange = (e) => {
    const val = e?.target?.value;
    setEmail?.(val);
    if (emailTouched) {
      const err = validateEmail(val);
      setEmailError(err);
      setEmailValid(!err);
    }
  };

  const triggerShake = () => {
    setShake(true);
    setTimeout(() => setShake(false), 600);
  };

  const handleSubmit = (e) => {
    e?.preventDefault();
    setEmailTouched(true);
    const err = validateEmail(email);
    setEmailError(err);
    setEmailValid(!err);
    if (err) {
      triggerShake();
      return;
    }
    onLoad?.();
  };

  const getBorderColor = () => {
    if (!emailTouched) return 'rgba(255,255,255,0.1)';
    if (emailError) return '#E8630A';
    if (emailValid) return '#22C55E';
    return 'rgba(255,255,255,0.1)';
  };

  return (
    <>
      <style>{`
        @keyframes emailShake {
          0%, 100% { transform: translateX(0); }
          15% { transform: translateX(-6px); }
          30% { transform: translateX(6px); }
          45% { transform: translateX(-5px); }
          60% { transform: translateX(5px); }
          75% { transform: translateX(-3px); }
          90% { transform: translateX(3px); }
        }
        .email-shake { animation: emailShake 0.6s ease; }
      `}</style>

      <div style={{
        background: '#0A1220',
        border: '1px solid rgba(255,255,255,0.06)',
        padding: '40px',
        position: 'relative',
        overflow: 'hidden',
        marginBottom: '32px'
      }}>
        {/* Color block accent */}
        <div style={{
          position: 'absolute', top: 0, left: 0,
          width: '4px', height: '100%',
          background: 'linear-gradient(to bottom, #E8630A, #3B6FD4)'
        }} />

        {/* Background texture number */}
        <div style={{
          position: 'absolute', right: '-10px', top: '-20px',
          fontFamily: 'Impact, Arial Black, sans-serif',
          fontSize: '120px', fontWeight: 900,
          color: 'rgba(59,111,212,0.04)',
          lineHeight: 1, userSelect: 'none', pointerEvents: 'none'
        }}>01</div>

        <div style={{ paddingLeft: '20px', position: 'relative', zIndex: 1 }}>
          <div style={{
            fontFamily: 'Syne, sans-serif',
            fontSize: '0.65rem', fontWeight: 700,
            color: '#E8630A', letterSpacing: '0.2em',
            textTransform: 'uppercase', marginBottom: '12px'
          }}>STEG 01 — IDENTIFISER DEG</div>

          <div style={{
            fontFamily: 'Impact, Arial Black, sans-serif',
            fontSize: 'clamp(22px, 3vw, 32px)',
            fontWeight: 900, color: '#EEF0F2',
            letterSpacing: '-0.02em', textTransform: 'uppercase',
            marginBottom: '8px'
          }}>DIN E-POSTADRESSE</div>

          <p style={{
            fontFamily: 'Georgia, serif', fontStyle: 'italic',
            fontSize: '0.9375rem', color: 'rgba(138,149,163,0.7)',
            lineHeight: 1.6, marginBottom: '28px', maxWidth: '480px'
          }}>
            Skriv inn e-postadressen du abonnerer med for å laste inn dine nåværende innstillinger.
          </p>

          <form onSubmit={handleSubmit} noValidate>
            <div
              className={shake ? 'email-shake' : ''}
              style={{ display: 'flex', gap: '0', maxWidth: '520px' }}
            >
              <div style={{ position: 'relative', flex: 1 }}>
                <div style={{
                  position: 'absolute', left: '14px', top: '50%',
                  transform: 'translateY(-50%)', pointerEvents: 'none'
                }}>
                  <Icon name="Mail" size={15} color="rgba(138,149,163,0.5)" />
                </div>
                <input
                  ref={inputRef}
                  type="email"
                  value={email}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  placeholder="din@epost.no"
                  style={{
                    width: '100%',
                    padding: '14px 40px 14px 42px',
                    background: 'rgba(255,255,255,0.04)',
                    border: `1px solid ${getBorderColor()}`,
                    borderRight: 'none',
                    color: '#EEF0F2',
                    fontFamily: 'Inter, sans-serif',
                    fontSize: '0.9375rem',
                    outline: 'none',
                    boxSizing: 'border-box',
                    transition: 'border-color 200ms ease'
                  }}
                  onFocus={(e) => {
                    if (!emailError) e.target.style.borderColor = 'rgba(232,99,10,0.5)';
                  }}
                />
                {/* Right-side status icon inside input */}
                {emailTouched && (
                  <div style={{
                    position: 'absolute', right: '12px', top: '50%',
                    transform: 'translateY(-50%)', pointerEvents: 'none'
                  }}>
                    {emailValid ? (
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#22C55E" strokeWidth="2.5" strokeLinecap="square">
                        <polyline points="20 6 9 17 4 12" />
                      </svg>
                    ) : (
                      <Icon name="AlertCircle" size={15} color="#E8630A" />
                    )}
                  </div>
                )}
              </div>
              <button
                type="submit"
                disabled={loading || !email?.trim()}
                style={{
                  padding: '14px 24px',
                  background: loading || !email?.trim() ? 'rgba(232,99,10,0.4)' : '#E8630A',
                  border: 'none',
                  color: '#EEF0F2',
                  fontFamily: 'Syne, sans-serif',
                  fontSize: '0.8125rem',
                  fontWeight: 700,
                  letterSpacing: '0.08em',
                  textTransform: 'uppercase',
                  cursor: loading || !email?.trim() ? 'not-allowed' : 'pointer',
                  display: 'flex', alignItems: 'center', gap: '8px',
                  whiteSpace: 'nowrap',
                  transition: 'background 150ms ease'
                }}
                onMouseEnter={(e) => { if (!loading && email?.trim()) e.currentTarget.style.background = '#CF5508'; }}
                onMouseLeave={(e) => { if (!loading && email?.trim()) e.currentTarget.style.background = '#E8630A'; }}
              >
                {loading ? (
                  <><Icon name="Loader" size={13} color="#EEF0F2" /> Laster...</>
                ) : (
                  <><Icon name="Search" size={13} color="#EEF0F2" /> Last inn</>
                )}
              </button>
            </div>

            {/* Inline email error message */}
            {emailTouched && emailError && (
              <div style={{
                display: 'flex', alignItems: 'center', gap: '7px',
                marginTop: '8px', maxWidth: '520px'
              }}>
                <Icon name="AlertCircle" size={12} color="#E8630A" />
                <span style={{
                  fontFamily: 'Inter, sans-serif',
                  fontSize: '0.8rem',
                  color: '#E8630A',
                  letterSpacing: '0.01em'
                }}>{emailError}</span>
              </div>
            )}

            {/* Valid email confirmation */}
            {emailTouched && emailValid && !emailError && (
              <div style={{
                display: 'flex', alignItems: 'center', gap: '7px',
                marginTop: '8px', maxWidth: '520px'
              }}>
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#22C55E" strokeWidth="2.5" strokeLinecap="square">
                  <polyline points="20 6 9 17 4 12" />
                </svg>
                <span style={{
                  fontFamily: 'Inter, sans-serif',
                  fontSize: '0.8rem',
                  color: '#22C55E',
                  letterSpacing: '0.01em'
                }}>Gyldig e-postadresse</span>
              </div>
            )}
          </form>

          {error && (
            <div style={{
              display: 'flex', alignItems: 'center', gap: '8px',
              marginTop: '14px'
            }}>
              <Icon name="AlertCircle" size={13} color="#E8630A" />
              <span style={{ fontFamily: 'Inter, sans-serif', fontSize: '0.8125rem', color: '#E8630A' }}>
                {error}
              </span>
            </div>
          )}

          {loaded && !error && (
            <div style={{
              display: 'flex', alignItems: 'center', gap: '8px',
              marginTop: '14px'
            }}>
              <Icon name="CheckCircle" size={13} color="#3B6FD4" />
              <span style={{ fontFamily: 'Inter, sans-serif', fontSize: '0.8125rem', color: '#3B6FD4' }}>
                Innstillinger lastet inn.
              </span>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default EmailIdentifier;
